import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Copy, Check } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Aurora } from "@/components/motion/Aurora";

export default function NewReservation() {
  const navigate = useNavigate();
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    pickup_date: "",
    pickup_time: "10:00",
    return_date: "",
    return_time: "18:00",
    pickup_location: "Madrid",
    return_location: "Madrid",
    deposit_amount: 1500,
    total_amount: 0,
    km_included_per_day: 200,
    extra_km_rate: 0.3,
    notes: "",
    rental_charged_externally: false,
    whatsapp_opt_in: true,
    locale: "es",
    self_service: true,
    // Señal al reservar (% del alquiler). Se rellena con el ajuste global y
    // queda congelado en la reserva al crearla.
    signal_percent: 30,
  });

  useEffect(() => {
    api.get("/admin/payment-settings")
      .then(({ data }) => setForm((f) => ({ ...f, signal_percent: Number(data.signal_percent) })))
      .catch(() => {});
  }, []);

  const update = (k) => (e) => {
    const v =
      e?.target?.type === "number" ? Number(e.target.value) : e?.target?.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.return_date < form.pickup_date) {
      toast.error("La fecha de devolución no puede ser anterior a la de recogida");
      return;
    }
    if (form.return_date === form.pickup_date && form.return_time <= form.pickup_time) {
      toast.error("Si la devolución es el mismo día, la hora debe ser posterior a la de recogida");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/reservations", form);
      setCreated(data);
      toast.success("Reserva creada");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const link = created
    ? `${window.location.origin}/reserva/${created.code}`
    : "";

  const copyLink = async () => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (created) {
    return (
      <div className="aurora-page relative isolate min-h-screen bg-ivory">
        <Aurora variant="subtle" fixed />
        <div className="container-x py-12 max-w-2xl">
          <div className="bg-white border border-sand rounded-xl p-8">
            <div className="h-12 w-12 rounded-full bg-ok/15 text-ok flex items-center justify-center mb-4">
              <Check className="h-6 w-6" />
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-charcoal mb-2">
              Reserva creada
            </h1>
            <p className="text-warmgray mb-6">
              Comparte este código o enlace con {created.client_name} para que
              pueda hacer su check-in.
            </p>

            <div className="bg-ivory border border-sand rounded-xl p-5 mb-3 text-center">
              <div className="text-overline mb-2">Código</div>
              <div className="font-mono text-3xl sm:text-4xl font-bold tracking-widest text-forest-deep" data-testid="created-code">
                {created.code}
              </div>
            </div>

            <button
              onClick={copyLink}
              className="w-full bg-forest-deep hover:bg-forest text-white rounded-lg px-4 py-3 flex items-center justify-between gap-3 transition-colors"
              data-testid="created-copy-link"
            >
              <span className="text-sm truncate">{link}</span>
              {copied ? (
                <Check className="h-4 w-4 shrink-0" />
              ) : (
                <Copy className="h-4 w-4 shrink-0" />
              )}
            </button>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setCreated(null);
                  setForm((f) => ({ ...f, client_name: "", client_email: "", client_phone: "", notes: "" }));
                }}
                className="border-sand"
                data-testid="created-new-btn"
              >
                Crear otra
              </Button>
              <Button
                onClick={() => navigate(`/admin/reservations/${created.id}`)}
                className="bg-forest-deep hover:bg-forest text-white"
                data-testid="created-view-btn"
              >
                Ver detalle
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="aurora-page relative isolate min-h-screen bg-ivory">
      <Aurora variant="subtle" fixed />
      <header className="bg-white border-b border-sand">
        <div className="container-x py-4 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center text-warmgray hover:text-forest-deep" data-testid="new-back-link">
            <ArrowLeft className="h-4 w-4 mr-2" /> Panel
          </Link>
        </div>
      </header>

      <main className="container-x py-8 max-w-3xl">
        <span className="text-overline">Nueva reserva</span>
        <h1 className="font-display text-3xl font-bold tracking-tight text-charcoal mt-2 mb-8">
          Crear reserva y generar código
        </h1>

        <form onSubmit={submit} className="space-y-8" data-testid="new-reservation-form">
          <section className="bg-white border border-sand rounded-xl p-6">
            <h2 className="font-display text-lg font-semibold text-charcoal mb-4">Cliente</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Nombre completo</Label>
                <Input required value={form.client_name} onChange={update("client_name")} className="mt-2 bg-ivory border-sand" data-testid="new-client-name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" required value={form.client_email} onChange={update("client_email")} className="mt-2 bg-ivory border-sand" data-testid="new-client-email" />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input required value={form.client_phone} onChange={update("client_phone")} className="mt-2 bg-ivory border-sand" data-testid="new-client-phone" />
              </div>
              <div>
                <Label>Idioma de los avisos</Label>
                <select
                  value={form.locale}
                  onChange={update("locale")}
                  className="mt-2 h-10 w-full rounded-md border border-sand bg-ivory px-3 text-sm"
                  data-testid="new-locale"
                >
                  <option value="es">Castellano</option>
                  <option value="ca">Català</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="flex items-start gap-3 cursor-pointer mt-1">
                  <Checkbox
                    checked={form.whatsapp_opt_in}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, whatsapp_opt_in: Boolean(v) }))}
                    data-testid="new-whatsapp-optin"
                  />
                  <span className="text-sm text-warmgray">
                    El cliente acepta recibir los avisos de la reserva también por
                    WhatsApp en ese teléfono (además del email).
                  </span>
                </label>
              </div>
            </div>
          </section>

          <section className="bg-white border border-sand rounded-xl p-6">
            <h2 className="font-display text-lg font-semibold text-charcoal mb-4">Fechas</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Fecha recogida</Label>
                <Input type="date" required value={form.pickup_date} onChange={update("pickup_date")} className="mt-2 bg-ivory border-sand" data-testid="new-pickup-date" />
              </div>
              <div>
                <Label>Hora recogida</Label>
                <Input type="time" required value={form.pickup_time} onChange={update("pickup_time")} className="mt-2 bg-ivory border-sand" data-testid="new-pickup-time" />
              </div>
              <div>
                <Label>Fecha devolución</Label>
                <Input type="date" required min={form.pickup_date || undefined} value={form.return_date} onChange={update("return_date")} className="mt-2 bg-ivory border-sand" data-testid="new-return-date" />
              </div>
              <div>
                <Label>Hora devolución</Label>
                <Input type="time" required value={form.return_time} onChange={update("return_time")} className="mt-2 bg-ivory border-sand" data-testid="new-return-time" />
              </div>
              <div>
                <Label>Lugar recogida</Label>
                <Input required value={form.pickup_location} onChange={update("pickup_location")} className="mt-2 bg-ivory border-sand" />
              </div>
              <div>
                <Label>Lugar devolución</Label>
                <Input required value={form.return_location} onChange={update("return_location")} className="mt-2 bg-ivory border-sand" />
              </div>
            </div>
          </section>

          <section className="bg-white border border-sand rounded-xl p-6">
            <h2 className="font-display text-lg font-semibold text-charcoal mb-4">Tarifas</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Fianza (€)</Label>
                <Input type="number" min="0" step="10" value={form.deposit_amount} onChange={update("deposit_amount")} className="mt-2 bg-ivory border-sand" data-testid="new-deposit" />
              </div>
              <div>
                <Label>Importe total (€)</Label>
                <Input type="number" min="0" step="1" value={form.total_amount} onChange={update("total_amount")} className="mt-2 bg-ivory border-sand" data-testid="new-total-amount" />
              </div>
              <div>
                <Label>Señal al reservar (%)</Label>
                <Input type="number" min="1" max="100" step="1" value={form.signal_percent} onChange={update("signal_percent")} className="mt-2 bg-ivory border-sand" data-testid="new-signal-percent" />
                <div className="text-xs text-warmgray-light mt-1">
                  {Number(form.total_amount) > 0
                    ? `${((Number(form.total_amount) * Number(form.signal_percent || 0)) / 100).toFixed(2)} € ahora · resto antes de la entrega`
                    : "Porcentaje del importe total que paga ahora el cliente"}
                </div>
              </div>
              <div>
                <Label>Km incluidos / día</Label>
                <Input type="number" min="0" value={form.km_included_per_day} onChange={update("km_included_per_day")} className="mt-2 bg-ivory border-sand" />
              </div>
              <div>
                <Label>Tarifa km extra (€)</Label>
                <Input type="number" min="0" step="0.01" value={form.extra_km_rate} onChange={update("extra_km_rate")} className="mt-2 bg-ivory border-sand" />
              </div>
              <div className="sm:col-span-2">
                <label className="flex items-start gap-3 cursor-pointer mt-1">
                  <Checkbox
                    checked={form.rental_charged_externally}
                    onCheckedChange={(v) =>
                      setForm((f) => ({ ...f, rental_charged_externally: Boolean(v) }))
                    }
                    data-testid="new-rental-external"
                  />
                  <span className="text-sm text-warmgray">
                    El alquiler ya está cobrado fuera de la plataforma (p. ej.
                    Yescapa). Si no lo marcas, el cliente paga ahora la señal y,
                    unos días antes de la entrega, recibe un correo para pagar el
                    resto junto con la fianza. Si la recogida es inminente
                    (devolución en menos de una semana), lo paga todo junto en el
                    pre-checkin, o podrás cobrarlo tú con la tarjeta guardada.
                  </span>
                </label>
              </div>
            </div>
          </section>

          <section className="bg-white border border-sand rounded-xl p-6">
            <h2 className="font-display text-lg font-semibold text-charcoal mb-4">Entrega</h2>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={form.self_service}
                onCheckedChange={(v) => setForm((f) => ({ ...f, self_service: Boolean(v) }))}
                data-testid="new-self-service"
              />
              <span className="text-sm text-warmgray">
                <strong className="text-charcoal">Recogida y devolución autónomas.</strong>{" "}
                El cliente hace la entrega desde su móvil junto a la furgoneta
                (verificaciones + fotos + GPS) y tú solo abres/cierras a
                distancia con la app We Connect cuando te llegue el aviso.
                Recuerda dejar la llave en su funda de la guantera.
              </span>
            </label>
          </section>

          <section className="bg-white border border-sand rounded-xl p-6">
            <Label>Notas internas</Label>
            <Textarea value={form.notes} onChange={update("notes")} className="mt-2 bg-ivory border-sand" rows={3} />
          </section>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate("/admin")} className="border-sand">
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} className="bg-forest-deep hover:bg-forest text-white px-8" data-testid="new-submit-btn">
              {loading ? "Creando..." : "Crear reserva y generar código"}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
